import axios from 'axios';
import { Capacitor, registerPlugin } from '@capacitor/core';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;
export const PRODUCT_ID = 'bliss_tile_222';
export const CHAT_PRODUCT_ID = 'bliss_global_chat_99';
export const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.bliss.arsh';
export const isAndroidApp = () => Capacitor.getPlatform() === 'android';

// Android browsers/webviews hand intent:// links to the Play Store app; other devices open the web listing.
export const openPlayStore = () => {
  if (/android/i.test(navigator.userAgent)) {
    window.location.href = 'intent://details?id=com.bliss.arsh#Intent;scheme=market;package=com.android.vending;'
      + `S.browser_fallback_url=${encodeURIComponent(PLAY_URL)};end`;
    return;
  }
  const w = window.open(PLAY_URL, '_blank', 'noopener');
  if (!w) window.location.href = PLAY_URL;
};

const BlissBilling = registerPlugin('BlissBilling');
let cached = null;
let cachedFor = null;
const listeners = new Set();
const authKey = () => axios.defaults.headers.common.Authorization || '';

// Cache is per signed-in account so switching accounts never keeps old locks.
export const getEntitlement = async (force = false) => {
  if (cached && !force && cachedFor === authKey()) return cached;
  if (cachedFor !== authKey()) cached = null;
  try {
    cachedFor = authKey();
    const { data } = await axios.get(`${API}/status`);
    cached = data;
  } catch (_) {
    cached = cached || { paywall_enabled: false, entitled: true, locked_tiles: [] };
  }
  listeners.forEach((fn) => fn(cached));
  return cached;
};
export const onEntitlement = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const verify = async (token, productId = PRODUCT_ID) => {
  const { data } = await axios.post(`${API}/google/verify`, { product_id: productId, purchase_token: token });
  cached = data;
  listeners.forEach((fn) => fn(cached));
  return data;
};

export const getPlayPrice = async (productId = PRODUCT_ID) => {
  if (!isAndroidApp()) return null;
  try {
    const { BillingPlugin } = await import('capacitor-billing');
    const res = await BillingPlugin.querySkuDetails({ product: productId, type: 'SUBS' });
    const d = typeof res.value === 'string' ? JSON.parse(res.value) : res;
    return d?.subscriptionOfferDetails?.[0]?.pricingPhases?.pricingPhaseList?.[0]?.formattedPrice || d?.price || null;
  } catch (_) { return null; }
};

const nativeVersion = async () => {
  try { const { App } = await import('@capacitor/app'); const i = await App.getInfo(); return `${i.version} (${i.build})`; }
  catch (_) { return 'unknown'; }
};

export const subscribe = async (productId = PRODUCT_ID) => {
  let p;
  if (Capacitor.isPluginAvailable('BlissBilling')) {
    p = await BlissBilling.subscribe({ productId });
  } else if (Capacitor.isPluginAvailable('BillingPlugin')) {
    const { BillingPlugin } = await import('capacitor-billing');
    const res = await BillingPlugin.launchBillingFlow({ product: productId, type: 'SUBS' });
    p = typeof res.value === 'string' ? JSON.parse(res.value) : res;
  } else {
    const names = ((window.Capacitor && window.Capacitor.PluginHeaders) || []).map((h) => h.name).join(', ');
    throw new Error(`Payment module missing in this app build (${await nativeVersion()}). Native plugins: ${names || 'none'}. Please update Bliss from the Play Store.`);
  }
  if (!p?.purchaseToken) throw new Error('No purchase received from Google Play');
  if (p.purchaseState === 'PENDING' || p.purchaseState === 2 || p.purchaseState === 4) return { pending: true };
  return verify(p.purchaseToken, productId);
};

export const restore = async (productId = PRODUCT_ID) => {
  const { purchases = [] } = await BlissBilling.queryActiveSubscriptions();
  const mine = purchases.filter((p) => p.purchaseState === 'PURCHASED' && (p.products || []).includes(productId));
  if (!mine.length) return null;
  let last = null;
  for (const p of mine) last = await verify(p.purchaseToken, productId);
  return last;
};
